import { useMemo, useState } from 'react';
import { CATEGORY_COLORS, categoryColor, type SkillDef } from '@/mocks/pipeline';
import { ioCardLabel, matchesPaletteQuery, paletteGroups } from '@/pages/home/skillPaletteGroups';

type Props = {
  skills: SkillDef[];
  onInsert: (skillId: string, prefix: string) => void;
  onInserted?: () => void;
  onSend?: () => void;
};

export default function SkillsActionList({ skills, onInsert, onInserted }: Props) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(
    () => skills.filter((s) => matchesPaletteQuery(s, query)),
    [skills, query],
  );
  const groups = useMemo(() => paletteGroups(filtered), [filtered]);

  const insert = (id: string) => {
    onInsert(id, `Use \`${id}\`: `);
    onInserted?.();
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-3 border-b border-border">
        <div className="text-[11px] uppercase tracking-wider text-texts mb-2 font-medium">Skills</div>
        <form role="search" autoComplete="off" onSubmit={(e) => e.preventDefault()} className="relative">
          <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-texts text-sm pointer-events-none" />
          <input
            type="search"
            data-testid="skill-search"
            name="skill-search"
            autoComplete="off"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
            placeholder="Search skills..."
            className="w-full appearance-none bg-surface border border-border rounded-md pl-8 pr-2 py-1.5 text-sm text-textp placeholder:text-texts outline-none focus:border-accent"
          />
        </form>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-3">
        {groups.map(({ discipline, skills: items }) => {
          if (items.length === 0) return null;
          return (
            <div key={discipline} data-testid={`skill-group-${discipline}`}>
              <div className="px-2 py-1 text-[11px] uppercase tracking-wider text-texts flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: CATEGORY_COLORS[discipline] || categoryColor(discipline) }} />
                {discipline}
              </div>
              <div className="mt-1 space-y-1.5">
                {items.map((s) => (
                  <button
                    type="button"
                    key={s.id}
                    data-testid={`skill-card-${s.id}`}
                    onClick={() => insert(s.id)}
                    className="w-full text-left group bg-surface hover:bg-surface2 border border-border hover:border-accent/50 rounded-lg p-2.5 transition-colors"
                  >
                    <div className="flex items-start gap-2">
                      <div
                        className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
                        style={{ background: `${(CATEGORY_COLORS[s.category] || categoryColor(s.category))}22`, color: CATEGORY_COLORS[s.category] || categoryColor(s.category) }}
                      >
                        <i className={`${s.icon || 'ri-cpu-line'} text-sm`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-semibold text-textp truncate">{s.name}</div>
                        <div className="text-[11px] text-texts leading-snug line-clamp-2">{s.description}</div>
                        <div className="text-[10px] text-texts/70 mt-0.5">{ioCardLabel(s)}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
